import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, UserSearch } from "lucide-react";
import { Sports } from "@/api/sports";
import { AuthExpiredError } from "@/api/client";
import { PageHeader, Skeleton, PlayerName } from "@/components/ui-kit";
import { DataState, SourceNote } from "@/components/DataState";
import { CURRENT_SEASON } from "@/lib/sports";

const LEAGUES = ["ALL", "NFL", "NCAA"];
const PAGE = 40;

function PlayerCard({ p }) {
  const meta = [p.teamAbbr || p.teamName, p.position, p.jersey != null ? `#${p.jersey}` : null].filter(Boolean).join(" · ");
  const body = (
    <>
      <div className="flex items-center gap-3 min-w-0">
        {p.headshotUrl
          ? <img src={p.headshotUrl} alt="" className="w-9 h-9 rounded-full object-cover bg-black/30 shrink-0" />
          : <span className="w-9 h-9 rounded-full bg-[#24362A] grid place-items-center text-white/70 text-xs font-display shrink-0">{(p.name || "?").slice(0, 2).toUpperCase()}</span>}
        <div className="min-w-0">
          <div className="text-sm text-white truncate">{p.name || "Unnamed player"}</div>
          <div className="text-[11px] text-white/45 truncate">{meta || "—"}</div>
        </div>
      </div>
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/55 shrink-0">{p.league || "—"}</span>
    </>
  );
  const cls = "tt-panel rounded-xl px-3 py-2.5 flex items-center justify-between gap-2";
  return p.id
    ? <Link to={`/player/${encodeURIComponent(p.id)}`} className={`${cls} hover:border-[#3A4859] transition-colors`} data-testid={`player-card-${p.id}`}>{body}</Link>
    : <div className={cls} data-testid="player-card-plain">{body}</div>;
}

export default function PlayersPage() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [league, setLeague] = useState("ALL");
  const [res, setRes] = useState(null);       // { state, players, nextCursor, season, coverage }
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);

  // Debounce the search box (300ms) so typing doesn't hammer the backend.
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const fetchPage = useCallback((cursor) => {
    const id = ++seq.current;
    const params = { q: debouncedQ || undefined, league: league === "ALL" ? undefined : league, season: CURRENT_SEASON, limit: PAGE, cursor };
    if (cursor) setLoadingMore(true); else setLoading(true);
    Sports.playersDirectory(params)
      .then((r) => {
        if (id !== seq.current) return; // stale
        setRes(r);
        setItems((prev) => (cursor ? [...prev, ...r.players] : r.players));
      })
      .catch((e) => { if (id === seq.current && !(e instanceof AuthExpiredError)) setRes({ state: "error", players: [], nextCursor: null }); })
      .finally(() => { if (id === seq.current) { setLoading(false); setLoadingMore(false); } });
  }, [debouncedQ, league]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  const changeLeague = (l) => { if (l !== league) { setLeague(l); setItems([]); } };
  const cov = res?.coverage;
  const partial = cov && (cov.partial === true || cov.roster_supported === false);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Players" subtitle="Search verified NFL & college-football players — current season." right={<UserSearch className="text-[#4ea152]" />} />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players by name" data-testid="players-search"
            className="w-full h-11 pl-9 pr-3 bg-[#131a24] border border-[#2A3544] rounded-lg text-white placeholder:text-white/30 focus:border-[#2F7D32] outline-none transition-colors" />
        </div>
        <div className="flex gap-2">
          {LEAGUES.map((l) => (
            <button key={l} onClick={() => changeLeague(l)} data-testid={`players-league-${l}`}
              className={`px-4 h-11 rounded-lg text-sm font-display tracking-wide transition-colors ${league === l ? "bg-[#2F7D32] text-white" : "tt-panel text-white/60 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {partial && (
        <div className="mb-4 rounded-lg border border-[#3a2f14] bg-[#241d0e] px-3 py-2.5 text-[12px] text-[#F4B740]" data-testid="players-coverage-note">
          {cov.note || "Complete NCAAF team rosters are not supported upstream — college results may be partial. Verified players only."}
        </div>
      )}

      {loading ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : (res && (res.state === "feature_unavailable" || res.state === "error")) ? (
        <DataState state={res.state} onRetry={() => fetchPage(null)} title={res.state === "error" ? "Couldn't load players" : "Players directory not available yet"} detail={res.state === "feature_unavailable" ? "This is being connected to live data. Check back soon." : undefined} />
      ) : items.length === 0 ? (
        <DataState state="empty" title="No players found" detail={debouncedQ ? `No verified players match “${debouncedQ}”.` : "No players available for this filter yet."} data-testid="players-empty" />
      ) : (
        <>
          <div className="text-[11px] text-white/40 mb-3" data-testid="players-count">Showing {items.length} player{items.length === 1 ? "" : "s"}{res?.nextCursor ? "+" : ""}</div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="players-grid">
            {items.map((p, i) => <PlayerCard key={p.id || `plain-${i}`} p={p} />)}
          </div>
          {res?.nextCursor && (
            <div className="flex justify-center mt-6">
              <button onClick={() => fetchPage(res.nextCursor)} disabled={loadingMore} data-testid="players-load-more"
                className="px-5 h-10 rounded-full text-sm font-display tracking-wide tt-panel text-white/80 hover:text-white disabled:opacity-50 transition-colors">
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
          <SourceNote timestamp={cov?.refreshed_at} coverage={`Season ${res?.season ?? CURRENT_SEASON} · verified provider feed`} />
        </>
      )}
    </div>
  );
}
